// Pane factory — encapsulates everything that is per-map: the OpenLayers Map,
// its full layer stack (basemaps, 4 content layers, vector overlays), the
// per-pane GPS marker features, and the per-pane mutable selection state
// (VISIBLE / ACTIVE_LAYERS / LAYER_IN_RANGE).
//
// Split-screen renders N panes that all share ONE `ol/View` (passed in) so they
// pan/zoom in lockstep — the canonical OpenLayers "shared view" pattern. The
// global clock (in radar.js) drives every pane's FramePools off the same
// startDate. FramePools are NOT built here: radar.js owns them (it imports
// FramePool and wires the timeline-aggregation callbacks) and attaches them to
// `pane.framePools` via buildPanePools().
//
// pane 0 is the original single map; its VISIBLE/ACTIVE_LAYERS/LAYER_IN_RANGE
// objects are the same instances radar.js aliases as its module globals, so the
// existing single-map code path is byte-for-byte unchanged in 1-up.

import { Map } from 'ol';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import VectorTileLayer from 'ol/layer/VectorTile';
import XYZ from 'ol/source/XYZ';
import ImageWMS from 'ol/source/ImageWMS';
import VectorTileSource from 'ol/source/VectorTile';
import GeoJSON from 'ol/format/GeoJSON';
import MVT from 'ol/format/MVT';
import Vector from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { Fill, Style } from 'ol/style';
import { gpsPositionStyle } from './ais/ownShipStyle';
import { track } from './analytics';
import { createGetMapSizeGuard } from './wms/requestShape';
import turnpointsUrl from './data/turnpoints-finland.geojson';
import railwayStationsUrl from './data/railway-stations-finland.geojson';
import railwayTracksUrl from './data/railway-tracks-finland.geojson';

// Railway stations swap their short code for the full station name at or below
// this resolution (map units per pixel in EPSG:3857) — about z9 and closer.
// Wider than that the code keeps the layer from printing each town's name a
// second time next to the place-name label; from here in there is room for the
// real name, and it is the more useful label once you are looking at a region
// rather than the whole network.
const RAILWAY_NAME_MAX_RESOLUTION = 320;

// Aerodromes add their name under the ICAO code at the same zoom the railway
// stations spell themselves out, so the two POI layers change at one point
// rather than at two. The code stays either way: it is what an aerodrome is
// called on a chart and on the radio, and it is the shorter thing to read once
// you already know which airfield you are looking at.
const AIRFIELD_NAME_MAX_RESOLUTION = 320;
// …and drop the code too once the map is wider than z6, leaving the discs on
// their own. Resolutions here are EPSG:3857 map units per pixel, where z6 is
// 2446 and z7 is 1223, so this cut sits between them: labels from z7 in,
// nothing but marks at z6 and out.
const AIRFIELD_LABEL_MAX_RESOLUTION = 1500;
// Two lines need the label lifted clear of the mark, since the text block is
// centred on its offset — at -16 the lower line would sit on the disc.
const AIRFIELD_LABEL_OFFSET = -16;
const AIRFIELD_LABEL_OFFSET_TWO_LINE = -23;

// The own-position marker + grey accuracy disc. One pair per pane so the
// marker can render in every pane; the ownLocation controller updates each
// pane's geometry (and swaps the style when the AIS source is active).
function makePositionFeatures() {
  const positionFeature = new Feature();
  positionFeature.setStyle(gpsPositionStyle);

  const accuracyFeature = new Feature();
  accuracyFeature.setStyle(new Style({
    fill: new Fill({ color: [128, 128, 128, 0.3] }),
  }));

  return { positionFeature, accuracyFeature };
}

// Build one pane. `deps` carries the shared singletons the layers reference:
//   options          — wmsServerConfiguration, imageRatio, default layer names
//   radarSiteSource  — shared VectorSource feeding every pane's radarSiteLayer
//   radarStyle, icaoStyle, municipalityStyleLight, vesivaylatStyleFn,
//   vesivaylaAreaStyle, rangeStyle — shared Style objects / style functions
//   visible          — Set seed for this pane's VISIBLE (used as-is, not cloned)
//   activeLayers     — object seed for this pane's ACTIVE_LAYERS (used as-is)
//   activeStyles     — object seed for this pane's ACTIVE_STYLES (used as-is)
export default function createPane(targetEl, sharedView, deps) {
  const {
    options,
    radarSiteSource,
    radarStyle,
    icaoStyle,
    turnpointStyle,
    railwayStyle,
    railwayTrackStyle,
    municipalityStyleLight,
    vesivaylatStyleFn,
    vesivaylaAreaStyle,
    rangeStyle,
    // Layer factories for the EDR-backed vector layers (observations, FMI
    // lightning). radar.js owns the controllers; panes just host the layers.
    createObservationLayer,
    createLightningLayer,
    // Place-name labels (src/placeNames.js) — panes share one VectorSource.
    createPlaceNamesLayer,
    // Storm cells (src/stormCells.js) — panes share one VectorSource; the
    // controller in radar.js owns fetching and the clock-driven advection.
    createStormCellsLayer,
    // Wind particles (src/particles/windParticles.js) — an overlay canvas per
    // pane, animated by the controller from its shared WebGL context.
    createWindLayer,
    // Traffic announcements (src/trafficMessages.js) — panes share one
    // VectorSource; the controller owns fetching and the clock filter.
    createTrafficLayer,
    createWeatherWarningsLayer,
    // Road weather cameras (src/weatherCameras.js) — panes share one
    // VectorSource; the controller owns the station list and the image panel.
    createWeatherCameraLayer,
    // Live OGN aircraft (src/gliders.js) — panes share one VectorSource; the
    // controller owns the WebSocket.
    createGliderLayer,
    // Active radar layer coverage outline (src/layerBbox.js) — per-pane,
    // since split panes can show different radar products.
    createLayerBboxLayer,
    // Live train positions (src/trainLocations.js) — panes share one
    // VectorSource; the controller owns the MQTT subscription.
    createTrainLocationLayer,
    // Airspace (src/airspace.js) — called once per part; panes share the three
    // VectorSources the controller fills from one bundled snapshot.
    createAirspaceLayer,
    // Aerodromes (src/airfields.js) — panes share one VectorSource fed from the
    // per-country eAIP snapshots; the caller supplies the label style function.
    createAirfieldLayer,
    // Search-and-rescue vessels (src/ais/rescueVessels.js) — panes share one
    // VectorSource; the controller owns the AIS subscription.
    createRescueVesselLayer,
    // METAR station plots (src/metar/metarLayer.js) — panes share one
    // VectorSource; the controller owns the fetching and follows the clock.
    createMetarLayer,
    // Place-search highlight pulse (src/search/searchHighlight.js) — panes
    // share one VectorSource so the pulse shows in every split-screen pane.
    createSearchHighlightLayer,
    visible,
    activeLayers,
    activeStyles = {},
    layerInRange = {},
    // For pane 0 the caller passes its existing module-global framePools object
    // so radar.js's `framePools` const and `pane0.framePools` are one and the
    // same; new panes get a fresh holder. buildPanePools() fills it later.
    framePools = {
      satelliteLayer: null,
      radarLayer: null,
      lightningWmsLayer: null,
    },
    index = 0,
  } = deps;

  const VISIBLE = visible;
  const ACTIVE_LAYERS = activeLayers;
  // Per-category style memory, alongside the sublayer memory: which colormap
  // this pane's radar (or any other multi-style category) should wear, kept
  // apart from the source params so it survives a product switch that cannot
  // honour it. Pane 0's is the one radar.js persists.
  const ACTIVE_STYLES = activeStyles;
  const LAYER_IN_RANGE = layerInRange;

  //
  // BASEMAPS
  //
  // Every raster source below requests images with CORS (crossOrigin:
  // 'anonymous') so the layer canvases stay untainted and the share tool can
  // export the map with canvas.toBlob(). One non-CORS draw taints a canvas
  // permanently. All the servers return Access-Control-Allow-Origin: *.
  const lightGrayBaseLayer = new TileLayer({
    visible: false,
    preload: Infinity,
    source: new XYZ({
      attributions: 'Tiles © <a href="https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer">ArcGIS</a>',
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      crossOrigin: 'anonymous',
    }),
  });

  const darkGrayBaseLayer = new TileLayer({
    preload: Infinity,
    source: new XYZ({
      attributions: 'Tiles © <a href="https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer">ArcGIS</a>',
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      crossOrigin: 'anonymous',
    }),
  });

  // Place-name labels replace the ArcGIS reference (label) tile layers: one
  // theme-agnostic vector layer whose style setMapLayer swaps, instead of two
  // rasters toggled per theme. Sits above the animated rasters but below the
  // lightning/observation vectors and every tool overlay.
  const placeNamesLayer = createPlaceNamesLayer();

  //
  // CONTENT LAYERS
  //
  // Drop-and-report guard for runaway base-source GetMaps (WIDTH/HEIGHT in
  // the tens of thousands — see requestShape.js MAX_GETMAP_DIM). The
  // telemetry captures the container/window geometry at the moment of the
  // oversize render so the layout transient causing it can be identified.
  const getMapGuard = (layerName) => createGetMapSizeGuard((w, h) => {
    console.warn(`Dropped oversize GetMap ${w}x${h} (${layerName}, pane ${index})`); // eslint-disable-line no-console
    track('oversize-getmap', {
      layer: layerName,
      size: `${w}x${h}`,
      el: `${targetEl.clientWidth}x${targetEl.clientHeight}`,
      win: `${window.innerWidth}x${window.innerHeight}`,
      dpr: window.devicePixelRatio,
      pane: index,
    });
  });

  const satelliteLayer = new ImageLayer({
    name: 'satelliteLayer',
    visible: VISIBLE.has('satelliteLayer'),
    opacity: 0.7,
    source: new ImageWMS({
      url: options.wmsServerConfiguration.eumetsat1.url,
      params: { FORMAT: 'image/jpeg', LAYERS: 'rgb_eview' },
      hidpi: false,
      attributions: 'EUMETSAT',
      ratio: options.imageRatio,
      serverType: 'geoserver',
      crossOrigin: 'anonymous',
      imageLoadFunction: getMapGuard('satelliteLayer'),
    }),
  });
  satelliteLayer.set('defaultFormat', 'image/jpeg');
  satelliteLayer.set('defaultTransparent', false);

  const radarLayer = new ImageLayer({
    name: 'radarLayer',
    visible: VISIBLE.has('radarLayer'),
    opacity: 0.7,
    source: new ImageWMS({
      url: options.wmsServerConfiguration.fi.url,
      params: { LAYERS: options.defaultRadarLayer },
      attributions: 'FMI (CC-BY-4.0)',
      ratio: options.imageRatio,
      hidpi: false,
      serverType: 'geoserver',
      // Lets the center-crosshair tool read the rendered radar pixel colour off
      // the canvas (getData) without tainting it. The meteocore WMS returns
      // Access-Control-Allow-Origin: *, so it's safe.
      crossOrigin: 'anonymous',
      imageLoadFunction: getMapGuard('radarLayer'),
    }),
  });
  radarLayer.set('defaultFormat', 'image/png');
  // Opt out of the webp wire format for radar specifically (see radar.js notes).
  radarLayer.set('disableWebp', true);

  // Flowing wind / precipitation-motion particles. NOT an OL layer and not in
  // the `layers` array below: it is an overlay canvas the controller animates
  // from its own frame loop (re-rendering OL per frame was what made the
  // radar stutter it), attached to this map's viewport by radar.js once the
  // Map exists. This handle only carries the POI visibility fan-out.
  const windLayer = createWindLayer(index);

  // FMI lightning is an EDR-backed vector layer (src/lightning/); this
  // companion raster carries the category's WMS products (li_afa/rdt from
  // view.eumetsat.int). Hidden until a WMS product is selected — the
  // lightning controller owns its visibility; radar.js owns its FramePool.
  const lightningLayer = createLightningLayer(index, VISIBLE.has('lightningLayer'));
  const lightningWmsLayer = new ImageLayer({
    name: 'lightningWmsLayer',
    visible: false,
    source: new ImageWMS({
      url: options.wmsServerConfiguration['mtg-li-afa'].url,
      params: { FORMAT: 'image/png', TRANSPARENT: 'TRUE', LAYERS: 'li_afa' },
      ratio: options.imageRatio,
      hidpi: false,
      serverType: 'geoserver',
      crossOrigin: 'anonymous',
      imageLoadFunction: getMapGuard('lightningWmsLayer'),
    }),
  });
  lightningWmsLayer.set('defaultFormat', 'image/png');
  lightningWmsLayer.set('_paneIndex', index);

  // Observations are an EDR-backed vector layer too (src/obs/); the wms-obs
  // GeoServer that once rendered both as rasters is permanently offline.
  const observationLayer = createObservationLayer(index, VISIBLE.has('observationLayer'));

  // Back-reference so radar.js can resolve which pane owns a layer when a
  // layer-level event (change:visible / propertychange) fires.
  satelliteLayer.set('_paneIndex', index);
  radarLayer.set('_paneIndex', index);
  lightningLayer.set('_paneIndex', index);
  observationLayer.set('_paneIndex', index);

  //
  // VECTOR OVERLAYS
  //
  const radarSiteLayer = new VectorLayer({
    source: radarSiteSource,
    style(feature) {
      radarStyle.getText().setText(feature.get('name'));
      return radarStyle;
    },
  });

  // Aerodromes come from src/airfields.js now that there is more than one
  // country's snapshot to read: panes share its source, and the METAR layer
  // reads the same records rather than parsing the files a second time.
  const icaoLayer = createAirfieldLayer((feature, resolution) => {
    const name = feature.get('name');
    const text = icaoStyle.getText();
    // Marks only once the map is wider than z6. Two countries' aerodromes is
    // 221 four-letter codes, and at synoptic zoom they stop being labels and
    // become a texture over the radar — the codes are unreadable at that size
    // anyway, and the discs alone still answer "is there an aerodrome there".
    if (resolution > AIRFIELD_LABEL_MAX_RESOLUTION) {
      text.setText('');
      return icaoStyle;
    }
    const withName = !!name && resolution <= AIRFIELD_NAME_MAX_RESOLUTION;
    text.setText(withName ? `${feature.get('icao')}\n${name}` : feature.get('icao'));
    text.setOffsetY(withName ? AIRFIELD_LABEL_OFFSET_TWO_LINE : AIRFIELD_LABEL_OFFSET);
    return icaoStyle;
  });

  const turnpointLayer = new VectorLayer({
    source: new Vector({
      format: new GeoJSON(),
      url: turnpointsUrl,
      attributions: 'Käännöspisteet © Ilmailuliitto',
    }),
    visible: false,
    // 423 points — declutter in their own group so labels thin out with zoom
    // without fighting the other decluttered layers (place names, obs).
    declutter: 'turnpoints',
    style(feature) {
      turnpointStyle.getText().setText(feature.get('name'));
      return turnpointStyle;
    },
  });

  // Railway main tracks — a bundled snapshot of Väylä's rail network,
  // regenerated by scripts/fetch-rail-tracks.mjs. Drawn before the stations so
  // the markers sit on top of their own lines. No labels, so no declutter
  // group: the geometry always draws.
  const railwayTrackLayer = new VectorLayer({
    source: new Vector({
      format: new GeoJSON(),
      url: railwayTracksUrl,
      attributions: 'Rataverkko © Väylävirasto',
    }),
    visible: false,
    style: railwayTrackStyle,
  });

  // Railway stations (Rautatieasemat) — a bundled snapshot of the passenger
  // stations from Digitraffic's rail metadata, regenerated by
  // scripts/fetch-rail-stations.mjs. Own declutter group so station labels
  // thin against each other rather than erasing place names.
  const railwayLayer = new VectorLayer({
    source: new Vector({
      format: new GeoJSON(),
      url: railwayStationsUrl,
      attributions: 'Rautatieasemat © Fintraffic (CC BY 4.0)',
    }),
    visible: false,
    declutter: 'railway-stations',
    style(feature, resolution) {
      // Zoomed out, the short code (HKI, TPE) — the airfields' ICAO pattern, and
      // for the same reason: nearly every station is named after the town it
      // sits in, so the name printed "Hämeenlinna" twice over, once from the
      // place-name layer and once from here. The codes are also what timetables
      // and platform signs use. Zoomed in there is room for the real name and
      // the station is visibly apart from the town label, so it wins.
      const zoomedIn = resolution <= RAILWAY_NAME_MAX_RESOLUTION;
      railwayStyle.getText().setText(feature.get(zoomedIn ? 'n' : 's'));
      return railwayStyle;
    },
  });

  const municipalityLayer = new VectorTileLayer({
    visible: false,
    renderMode: 'vector',
    source: new VectorTileSource({
      format: new MVT(),
      // MML Maastotietokanta `kunta` boundaries at full base-map resolution,
      // replacing the Tilastokeskus-derived fi-municipalities collection.
      url: 'https://meteocore.app.meteo.fi/tiles/collections/mml-maastotiedot-kunta/tiles/WebMercatorQuad/{z}/{y}/{x}?f=mvt',
      attributions: 'Kunnat © Maanmittauslaitos',
      maxZoom: 14,
    }),
    style: municipalityStyleLight,
  });

  const vesivaylaAreaLayer = new VectorLayer({
    visible: false,
    source: new Vector({
      format: new GeoJSON(),
      url: 'https://avoinapi.vaylapilvi.fi/vaylatiedot/ogc/features/v1/collections/vesivaylatiedot:vaylaalueet_uusi/items?f=application/geo%2Bjson&limit=10000',
      attributions: 'Väylävirasto',
    }),
    style: vesivaylaAreaStyle,
  });

  const vesivaylatLayer = new VectorLayer({
    visible: false,
    declutter: true,
    source: new Vector({
      format: new GeoJSON(),
      url: 'https://avoinapi.vaylapilvi.fi/vaylatiedot/ogc/features/v1/collections/vesivaylatiedot:vaylat_uusi/items?f=application/geo%2Bjson&limit=10000',
      attributions: 'Väylävirasto',
    }),
    style: vesivaylatStyleFn,
  });

  // Storm cells are data about the radar image, not map context, so they sit
  // above the context overlays (place names, sites, airfields, turnpoints) in
  // the `layers` array below — but still under the observation symbols and the
  // own-position marker, which the user is usually looking for specifically.
  const stormCellsLayer = createStormCellsLayer();

  // Traffic announcements are road context rather than data about the radar
  // image, so they sit below the storm cells and observation symbols — but
  // above the basemap context overlays, since an incident marker the user is
  // hunting for must not end up under a place-name label.
  const trafficLayer = createTrafficLayer();
  const weatherWarningsLayer = createWeatherWarningsLayer();

  // Road weather cameras sit just above the traffic announcements — same road
  // context, and a camera marker is a tap target the user is hunting for, so it
  // should not end up under an incident marker.
  const weatherCameraLayer = createWeatherCameraLayer();

  // Live aircraft sit above the static context layers — they move, and they are
  // what the user switched the layer on to watch.
  const gliderLayer = createGliderLayer();

  // Radar coverage outline: above the radar image and the draw-tool guide
  // layer so the dashed edge always reads, below the point markers it would
  // otherwise visually compete with.
  const { layer: layerBboxLayer, setBbox: setLayerBbox } = createLayerBboxLayer();

  // Live trains, for the same reason, and above the track network and stations
  // they run on: a train hidden under a station disc is the one thing on the
  // Rautatiet layer that is actually moving.
  const trainLocationLayer = createTrainLocationLayer();

  // Airspace sits BELOW every point marker — the radar sites, the aerodromes,
  // the turnpoints. It is context for them: an aerodrome inside a CTR is the
  // thing being looked at, and a boundary drawn over its mark would both hide it
  // and take the tap meant for it. Within the group, reservation areas
  // underneath, being the largest and least specific, then the restrictions,
  // then controlled airspace on top.
  const airspaceReservedLayer = createAirspaceLayer('reserved');
  const airspaceRestrictedLayer = createAirspaceLayer('restricted');
  const airspaceControlledLayer = createAirspaceLayer('controlled');

  // Rescue vessels sit with the other live movers, above the static context.
  const rescueVesselLayer = createRescueVesselLayer();

  // METAR plots annotate the aerodromes, so they sit above the airspace and the
  // aerodrome discs but below the things that move.
  const metarLayer = createMetarLayer();

  const guideLayer = new VectorLayer({
    source: new Vector(),
    style: rangeStyle,
  });

  const { positionFeature, accuracyFeature } = makePositionFeatures();
  const ownPositionLayer = new VectorLayer({
    visible: false,
    // Re-render during zoom/pan so the AIS symbol's pixel-sized geometries
    // (triangle, heading line — computed from the frame's resolution) stay a
    // constant screen size instead of scaling with the map and snapping back
    // when the gesture ends. One feature pair; per-frame restyle is cheap.
    updateWhileAnimating: true,
    updateWhileInteracting: true,
    source: new Vector({
      features: [accuracyFeature, positionFeature],
    }),
  });

  // Search highlight pulse: appended last in `layers` so the ring draws
  // above everything (labels, observations, own position).
  const searchHighlightLayer = createSearchHighlightLayer();

  const layerss = {
    satelliteLayer,
    radarLayer,
    observationLayer,
    lightningLayer,
  };

  const layers = [
    lightGrayBaseLayer,
    darkGrayBaseLayer,
    satelliteLayer,
    radarLayer,
    placeNamesLayer,
    // Keep place-name text from painting over the warning pictograms.
    weatherWarningsLayer,
    guideLayer,
    layerBboxLayer,
    lightningWmsLayer,
    lightningLayer,
    municipalityLayer,
    vesivaylaAreaLayer,
    vesivaylatLayer,
    airspaceReservedLayer,
    airspaceRestrictedLayer,
    airspaceControlledLayer,
    radarSiteLayer,
    icaoLayer,
    turnpointLayer,
    metarLayer,
    railwayTrackLayer,
    railwayLayer,
    trafficLayer,
    weatherCameraLayer,
    trainLocationLayer,
    rescueVesselLayer,
    gliderLayer,
    stormCellsLayer,
    ownPositionLayer,
    observationLayer,
    searchHighlightLayer,
  ];

  const map = new Map({
    target: targetEl,
    layers,
    controls: [],
    view: sharedView,
    keyboardEventTarget: document,
  });

  return {
    index,
    el: targetEl,
    map,
    view: sharedView,
    layers,
    layerss,
    // individual layer handles
    lightGrayBaseLayer,
    darkGrayBaseLayer,
    placeNamesLayer,
    satelliteLayer,
    radarLayer,
    windLayer,
    lightningLayer,
    lightningWmsLayer,
    observationLayer,
    airspaceReservedLayer,
    airspaceRestrictedLayer,
    airspaceControlledLayer,
    radarSiteLayer,
    icaoLayer,
    turnpointLayer,
    metarLayer,
    railwayTrackLayer,
    railwayLayer,
    trafficLayer,
    weatherCameraLayer,
    trainLocationLayer,
    rescueVesselLayer,
    gliderLayer,
    layerBboxLayer,
    setLayerBbox,
    stormCellsLayer,
    weatherWarningsLayer,
    municipalityLayer,
    vesivaylaAreaLayer,
    vesivaylatLayer,
    guideLayer,
    ownPositionLayer,
    searchHighlightLayer,
    positionFeature,
    accuracyFeature,
    // per-pane mutable state
    VISIBLE,
    ACTIVE_LAYERS,
    ACTIVE_STYLES,
    LAYER_IN_RANGE,
    // FramePools are attached later by radar.js buildPanePools()
    framePools,
    updateSize() { map.updateSize(); },
  };
}
